(() => {
  const CART_LINK = '.hb-global-header__cart';
  const ROOT = () => window.Shopify?.routes?.root || '/';

  async function getCart() {
    const response = await fetch(`${ROOT()}cart.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error('Unable to load cart');
    return response.json();
  }

  function money(cents, currency) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: currency || 'USD',
      }).format(cents / 100);
    } catch (_) {
      return `$${(cents / 100).toFixed(2)}`;
    }
  }

  async function refreshHeaderCart(cartData) {
    const cartLink = document.querySelector(CART_LINK);
    if (!cartLink) return;
    try {
      const cart = cartData || await getCart();
      let badge = cartLink.querySelector('.hb-global-header__cart-count');
      if (cart.item_count > 0) {
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'hb-global-header__cart-count';
          cartLink.appendChild(badge);
        }
        badge.textContent = cart.item_count;
      } else if (badge) badge.remove();
    } catch (_) {}
  }

  function ensureDrawer() {
    let overlay = document.querySelector('[data-hb-cart-drawer]');
    if (overlay) return overlay;
    overlay = document.createElement('div');
    overlay.className = 'hb-cart-drawer-overlay';
    overlay.dataset.hbCartDrawer = '';
    overlay.hidden = true;
    overlay.innerHTML = `
      <div class="hb-cart-drawer-backdrop" data-hb-cart-close></div>
      <aside class="hb-cart-drawer" role="dialog" aria-modal="true" aria-labelledby="hb-cart-drawer-title">
        <header class="hb-cart-drawer__header">
          <div><p class="hb-cart-drawer__eyebrow">HELLOBULGARIA</p><h2 id="hb-cart-drawer-title">Your cart</h2></div>
          <button type="button" class="hb-cart-drawer__close" data-hb-cart-close aria-label="Close cart">×</button>
        </header>
        <div class="hb-cart-drawer__body" data-hb-cart-body></div>
      </aside>`;
    document.body.appendChild(overlay);
    overlay.querySelectorAll('[data-hb-cart-close]').forEach(el => el.addEventListener('click', closeDrawer));
    document.addEventListener('keydown', e => { if (e.key === 'Escape') closeDrawer(); });
    return overlay;
  }

  function closeDrawer() {
    const overlay = document.querySelector('[data-hb-cart-drawer]');
    if (!overlay) return;
    overlay.hidden = true;
    document.documentElement.classList.remove('hb-cart-drawer-open');
  }

  async function renderDrawer() {
    const overlay = ensureDrawer();
    const body = overlay.querySelector('[data-hb-cart-body]');
    body.innerHTML = '<div class="hb-cart-drawer__loading">Loading cart…</div>';
    overlay.hidden = false;
    document.documentElement.classList.add('hb-cart-drawer-open');
    try {
      const cart = await getCart();
      refreshHeaderCart(cart);
      if (!cart.item_count) {
        body.innerHTML = `<div class="hb-cart-drawer__empty"><h3>Your cart is empty</h3><a href="${ROOT()}collections/all">Continue shopping</a></div>`;
        return;
      }
      const items = cart.items.map((item, index) => {
        const image = item.featured_image?.url || item.image || '';
        const originalUnit = item.original_price || item.price || item.final_price;
        const finalUnit = item.final_price;
        const hasDiscount = originalUnit > finalUnit;
        const discountPercent = hasDiscount ? Math.round((1 - (finalUnit / originalUnit)) * 100) : 0;
        const savings = hasDiscount ? (item.original_line_price - item.final_line_price) : 0;
        return `<article class="hb-cart-drawer__item">
          <a class="hb-cart-drawer__image" href="${item.url}">${image ? `<img src="${image}" alt="" loading="lazy">` : ''}</a>
          <div class="hb-cart-drawer__details">
            <a class="hb-cart-drawer__title" href="${item.url}">${item.product_title}</a>
            <div class="hb-cart-drawer__unit">${hasDiscount ? `<span class="hb-cart-drawer__compare">${money(originalUnit, cart.currency)}</span><strong>${money(finalUnit, cart.currency)}</strong><span class="hb-cart-drawer__discount-badge">${discountPercent}% OFF</span>` : money(finalUnit, cart.currency)}</div>
            ${hasDiscount ? `<div class="hb-cart-drawer__saving">You save ${money(savings, cart.currency)}</div>` : ''}
            <div class="hb-cart-drawer__quantity">
              <button type="button" data-hb-cart-change data-line="${index + 1}" data-qty="${Math.max(0,item.quantity - 1)}" aria-label="Decrease quantity">−</button>
              <span>${item.quantity}</span>
              <button type="button" data-hb-cart-change data-line="${index + 1}" data-qty="${item.quantity + 1}" aria-label="Increase quantity">+</button>
              <button type="button" class="hb-cart-drawer__remove" data-hb-cart-change data-line="${index + 1}" data-qty="0">Remove</button>
            </div>
          </div>
          <strong class="hb-cart-drawer__line-price">${money(item.final_line_price, cart.currency)}</strong>
        </article>`;
      }).join('');
      body.innerHTML = `<div class="hb-cart-drawer__items">${items}</div>
        <footer class="hb-cart-drawer__footer">
          <div class="hb-cart-drawer__subtotal"><span>Subtotal</span><strong>${money(cart.total_price, cart.currency)}</strong></div>
          ${cart.original_total_price > cart.total_price ? `<div class="hb-cart-drawer__total-saving"><span>Bundle discount</span><strong>−${money(cart.original_total_price - cart.total_price, cart.currency)}</strong></div>` : ''}
          <p>Taxes and shipping calculated at checkout.</p>
          <a class="hb-cart-drawer__checkout" href="${ROOT()}checkout">Check out</a>
          <a class="hb-cart-drawer__view-cart" href="${ROOT()}cart">View cart</a>
        </footer>`;
      body.querySelectorAll('[data-hb-cart-change]').forEach(btn => btn.addEventListener('click', async () => {
        btn.disabled = true;
        try {
          const response = await fetch(`${ROOT()}cart/change.js`, {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ line: Number(btn.dataset.line), quantity: Number(btn.dataset.qty) }),
          });
          if (!response.ok) throw new Error('Cart update failed');
          const cart = await response.json();
          await refreshHeaderCart(cart);
          await renderDrawer();
        } catch (_) { btn.disabled = false; }
      }));
    } catch (_) {
      body.innerHTML = `<div class="hb-cart-drawer__empty"><h3>We couldn't refresh the cart.</h3><a href="${ROOT()}cart">View cart</a></div>`;
    }
  }

  document.addEventListener('click', event => {
    const cartLink = event.target.closest(CART_LINK);
    if (!cartLink || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    renderDrawer();
  });

  const originalFetch = window.fetch;
  window.fetch = async function (...args) {
    const response = await originalFetch.apply(this, args);
    const input = args[0];
    const url = typeof input === 'string' ? input : input?.url || '';
    if (/\/cart\/(add|change|update|clear)(\.js)?(?:\?|$)/.test(url)) {
      response.clone().text().finally(() => window.setTimeout(refreshHeaderCart, 80));
    }
    return response;
  };

  document.addEventListener('cart:update', () => refreshHeaderCart());
  document.addEventListener('cart:refresh', () => refreshHeaderCart());
  document.addEventListener('product:added', () => refreshHeaderCart());
  window.addEventListener('pageshow', () => refreshHeaderCart());
})();
